import { normalizeVendor } from "./normalizeVendor";

export interface CategorizeInput {
  vendorNormalized: string;
  existingExpenses: Array<{ vendor: string; categoryId: string }>;
}

/**
 * Suggest a category for an incoming CSV row based on the user's expense
 * history.
 *
 * Strategy:
 *   1. Exact match on normalized vendor — pick the most frequently used
 *      categoryId among all matches.
 *   2. Substring match — look for any historical vendor whose normalized
 *      form contains this one, or vice versa. Among those, pick the
 *      categoryId associated with the longest-matched vendor (longer means
 *      more specific and typically a better signal).
 *   3. Otherwise return null.
 *
 * Returns null if the history contains no rows or nothing resembles the
 * incoming vendor.
 */
export function suggestCategory(input: CategorizeInput): string | null {
  const target = input.vendorNormalized.trim();
  if (!target || input.existingExpenses.length === 0) {
    return null;
  }

  // Pre-normalize all existing vendors once.
  const existing = input.existingExpenses.map((e) => ({
    normalized: normalizeVendor(e.vendor),
    categoryId: e.categoryId,
  }));

  // --- Pass 1: exact match ---
  const exactCounts = new Map<string, number>();
  for (const e of existing) {
    if (e.normalized === target && e.categoryId) {
      exactCounts.set(e.categoryId, (exactCounts.get(e.categoryId) ?? 0) + 1);
    }
  }
  if (exactCounts.size > 0) {
    // Return the most frequent categoryId. Stable in the face of ties: the
    // first one to reach the max count wins.
    let best: string | null = null;
    let bestCount = 0;
    for (const [cat, count] of exactCounts) {
      if (count > bestCount) {
        best = cat;
        bestCount = count;
      }
    }
    return best;
  }

  // --- Pass 2: substring match ---
  let bestSub: { categoryId: string; matchLen: number } | null = null;
  for (const e of existing) {
    if (!e.normalized || !e.categoryId) continue;
    let matchLen = 0;
    if (e.normalized.includes(target)) {
      matchLen = target.length;
    } else if (target.includes(e.normalized)) {
      matchLen = e.normalized.length;
    }
    if (matchLen > 0) {
      if (!bestSub || matchLen > bestSub.matchLen) {
        bestSub = { categoryId: e.categoryId, matchLen };
      }
    }
  }

  return bestSub ? bestSub.categoryId : null;
}
